import { useState, useEffect, useRef, useMemo } from "react";
import styles from "./Style.module.css";
import DropDownIcon from "../../Icons/DropDownIcon";
import SearchBar from "../../Search/SearchBar";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";
import DotIcon from "../../Icons/DotIcon";
// import logger from "../../../Utils/logger";

const RoomDropdown = ({
	options,
	onSelect,
	selectedLabel,
	selectedOption: externalSelectedOption,
	onDropdownToggle = null,
}) => {
	const [selectedOption, setSelectedOption] = useState({});
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);
	const hasExternalSelectedItem = Boolean(
		externalSelectedOption?.item &&
		(externalSelectedOption.item.item_id != null || externalSelectedOption.item.item_name)
	);
	const currentSelectedOption = hasExternalSelectedItem ? externalSelectedOption : selectedOption;

	useEffect(() => {
		if (hasExternalSelectedItem) {
			setSelectedOption(externalSelectedOption);
		}
	}, [hasExternalSelectedItem, externalSelectedOption]);

	const closeDropdown = () => {
		setDropdownOpen(false);
		if (onDropdownToggle) {
			onDropdownToggle(false);
		}
	};
	// Close dropdown when interacting outside, including calendar clicks/scrolls.
	useEffect(() => {
		if (!isDropdownOpen) return undefined;

		const handleOutsideInteraction = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				closeDropdown();
			}
		};

		document.addEventListener("pointerdown", handleOutsideInteraction, true);

		return () => {
			document.removeEventListener("pointerdown", handleOutsideInteraction, true);
		};
	}, [isDropdownOpen, onDropdownToggle]);

	// Auto-scroll dropdown into view inside scrollable parent
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			// Small delay to ensure the dropdown is rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	const handleSelect = (item) => {
		const selectedOptionData = { item: item };
		setSelectedOption(selectedOptionData);
		if (onSelect) {
			onSelect(item);
		}
		closeDropdown();
		setSearchQuery("");
	};

	
	// Filter options by capacity type, item name, and facilities
	const filteredOptions = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		// "couple" is the label users type for double-capacity rooms
		const isCoupleQuery = query.length >= 3 && ("couple".startsWith(query) || query.startsWith("couple"));
		const capacityQuery = isCoupleQuery ? "double" : query;

		return options
			.map((option) => {
				const capacityMatches = String(option?.capacity_type ?? "")
					.toLowerCase()
					.includes(capacityQuery);

				const filteredItems = option?.items?.filter((item) => {
					const itemNameMatches = String(item?.item_name ?? "")
						.toLowerCase()
						.includes(query);
					const facilities = Array.isArray(item?.facilities) ? item.facilities : [];
					const facilityMatches = facilities.some((facility) =>
						String(facility ?? "").toLowerCase().includes(query)
					);
					return itemNameMatches || facilityMatches;
				}) || [];

				// Capacity match keeps every bed in the room; otherwise keep matching beds only
				if (capacityMatches || filteredItems.length > 0) {
					return {
						...option,
						items: capacityMatches ? option.items : filteredItems
					};
				}
				return null;
			})
			.filter(Boolean);
	}, [options, searchQuery]);

	// Flatten items for keyboard navigation index calculation
	const visibleItems = useMemo(() => {
		const items = [];
		filteredOptions.forEach(option => {
			if (option?.items?.length > 0) {
				option.items.forEach(item => {
					items.push(item);
				});
			}
		});
		return items;
	}, [filteredOptions]);

	// Initialize keyboard navigation
	const {
		selectedIndex,
		setSelectedIndex,
		handleKeyDown: handleKeyboardNav,
		setIsKeyboardNavActive,
		isKeyboardNavActive
	} = useKeyboardNavigation(
		visibleItems.length,
		(item) => handleSelect(item),
		{ listRef: dropdownMenuRef }
	);

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (index) => visibleItems[index]);
	};

	return (
		<div
			ref={dropdownRef}
			className={`commonDropdownNoImage`}
			tabIndex={0}>
			<div className={`dropdownToggle cursor-pointer`}
				onClick={() => {
					const newState = !isDropdownOpen;
					setDropdownOpen(newState);
					if (onDropdownToggle) {
						onDropdownToggle(newState);
					}
				}}>
				{currentSelectedOption && currentSelectedOption?.item ? currentSelectedOption?.item?.item_name : 'Select Room' }
				{!selectedLabel && (
					<DropDownIcon 
						size={12} 
						color="#6b7280" 
						className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
					/>
				)}
		</div>
		{isDropdownOpen && (
			<div className={`${styles.dropdownContainer}`} ref={dropdownMenuRef}>
				{/* Search Bar */}
				<div className={styles.header}>
					<SearchBar
						ref={searchInputRef}
						placeholder="Search..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className="border-0"
					/>
				</div>
				<div className={`${styles.dropdownMenu}`}>
					{filteredOptions.length > 0 ? (
						(() => {
							let globalIndex = 0;
							return filteredOptions.map((option, index) => {
								// const availableItems = option?.items?.filter(item => item?.bookings?.length === 0)?.length || 0;
								return (
									<div
										key={index}
										className={`${styles.dropdownItem}`}
										>
										{/* <div className={styles.topHeading}>
											<h6>{option?.room_name ? option?.room_name : 'N/A'}</h6>
											{availableItems > 0 ? (
											<div className={`${styles.badge} ${styles.available}`}>
												{availableItems}
											</div>
											) : (
											<div className={`${styles.badge} ${styles.fullyBooked}`}><DotIcon size={12} /></div>
											)}
										</div> */}
										<div className={styles.bottomList}>
											{option?.items.length > 0 && option?.items.map((item, idx) => {
												const currentIndex = globalIndex++;
												const isSelected = selectedIndex === currentIndex;
												const hasBookings = Array.isArray(item?.bookings) && item.bookings.length > 0;
												return (
													<div className={`${styles.item} ${isSelected ? styles.selectedItem : ''}`} 
														key={idx}
														data-key-nav-index={currentIndex}
														onClick={() => handleSelect(item)}
														onMouseEnter={() => {
															if (!isKeyboardNavActive) {
																setSelectedIndex(currentIndex);
															}
														}}
														onMouseMove={() => {
															if (isKeyboardNavActive) {
																setIsKeyboardNavActive(false);
																setSelectedIndex(currentIndex);
															}
														}}
													>
														<div className={styles.left}>
															{/* <span className={styles.checkMark}></span> */}
															<p>{item?.item_name}</p>
														</div>
														<div className={styles.right}>
															{hasBookings ? <DotIcon size={12} /> : ""}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								);
							});
						})()
					) : (
						<div className={`${styles.dropdownItem} no-item-found-placeholder`}>
							No rooms found
						</div>
					)}
				</div>
			</div>
		)}
	</div>
	);
};

export default RoomDropdown;
